/**
 * QuotaCalculator - Frontend service for quota calculations and comparisons
 */
export class QuotaCalculator {
  constructor(quotaProject, dimensions, cells) {
    this.quotaProject = quotaProject;
    this.dimensions = dimensions;
    this.cells = cells || [];
  }

  // Overall quota summary
  getOverallSummary() {
    const totalCellTargets = this.cells.reduce((sum, cell) => 
      sum + (cell.target_count || 0), 0
    );
    const totalCurrent = this.cells.reduce((sum, cell) => 
      sum + (cell.current_count || 0), 0
    );
    const overallTarget = this.quotaProject?.overall_target || 0;

    return {
      overallTarget,
      totalAllocated: totalCellTargets,
      totalCurrent,
      remaining: overallTarget - totalCellTargets,
      percentageAllocated: overallTarget > 0 
        ? (totalCellTargets / overallTarget) * 100 
        : 0,
      percentageComplete: overallTarget > 0
        ? (totalCurrent / overallTarget) * 100
        : 0
    };
  }

  // Per-dimension breakdown
  getDimensionBreakdown(dimensionId) {
    const dimension = this.dimensions.find(d => d.id === dimensionId);
    if (!dimension) return null;

    const breakdown = {};

    // Initialize with all dimension values
    if (dimension.values_json && Array.isArray(dimension.values_json)) {
      dimension.values_json.forEach(value => {
        if (value.is_active !== false) {
          breakdown[value.code] = {
            label: value.label,
            code: value.code,
            allocated: 0,
            current: 0,
            cells: []
          };
        }
      });
    }

    // Calculate from cells
    this.cells.forEach(cell => {
      const dimensionValues = cell.dimension_values_json || {};
      const valueCode = dimensionValues[dimensionId];
      if (valueCode && breakdown[valueCode]) {
        breakdown[valueCode].allocated += cell.target_count || 0;
        breakdown[valueCode].current += cell.current_count || 0;
        breakdown[valueCode].cells.push(cell.id);
      }
    });

    // Calculate totals
    const totalAllocated = Object.values(breakdown)
      .reduce((sum, item) => sum + item.allocated, 0);
    const totalCurrent = Object.values(breakdown)
      .reduce((sum, item) => sum + item.current, 0);

    return {
      dimension: {
        id: dimension.id,
        label: dimension.label,
      },
      breakdown: Object.values(breakdown),
      totals: {
        allocated: totalAllocated,
        current: totalCurrent,
        remaining: (this.quotaProject?.overall_target || 0) - totalAllocated
      }
    };
  }

  // All dimensions breakdown
  getAllDimensionsBreakdown() {
    return this.dimensions.map(dim => ({
      dimension: dim,
      breakdown: this.getDimensionBreakdown(dim.id)
    }));
  }

  // Cell progress
  getCellProgress(cellId) {
    const cell = this.cells.find(c => c.id === cellId);
    if (!cell) return null;

    const target = cell.target_count || 0;
    const current = cell.current_count || 0;

    return {
      cell,
      progress: target > 0 ? (current / target) * 100 : 0,
      remaining: Math.max(0, target - current),
      isComplete: current >= target && target > 0
    };
  }

  // Validation warnings
  getValidationWarnings() {
    const warnings = [];
    const summary = this.getOverallSummary();

    // Exceeds target
    if (summary.totalAllocated > summary.overallTarget) {
      warnings.push({
        type: 'error',
        severity: 'high',
        message: `Total alloué (${summary.totalAllocated.toLocaleString()}) dépasse l'objectif global (${summary.overallTarget.toLocaleString()})`,
        difference: summary.totalAllocated - summary.overallTarget
      });
    }

    // Under-allocated
    if (summary.percentageAllocated < 80 && summary.overallTarget > 0) {
      warnings.push({
        type: 'warning',
        severity: 'medium',
        message: `${summary.percentageAllocated.toFixed(1)}% alloué. ${summary.remaining.toLocaleString()} restant(s).`
      });
    }

    // Zero target cells
    const zeroCells = this.cells.filter(c => !c.target_count || c.target_count === 0);
    if (zeroCells.length > 0) {
      warnings.push({
        type: 'info',
        severity: 'low',
        message: `${zeroCells.length} cellule(s) avec une cible de zéro`
      });
    }

    return warnings;
  }

  // Get cells matching dimension values
  getCellsByDimensionValues(dimensionValues) {
    return this.cells.filter(cell => {
      const cellValues = cell.dimension_values_json || {};
      return Object.keys(dimensionValues).every(dimId => {
        return cellValues[dimId] === dimensionValues[dimId];
      });
    });
  }
}

